/**
 * What the screen behind the assistant is about.
 *
 * # Why the portal carries it at all
 *
 * The assistant moved out of its own page and into a drawer over whatever the desk is
 * already doing (docs/AI.md § "Pomočnik v vrstici in zavedanje strani"). The moment it
 * did, the sentence a receptionist actually says became answerable — "premakni tega
 * gosta", "zapri to sobo" — and it is answerable only if the question carries the thing
 * the person is pointing at. Nothing else on the screen tells the service which booking
 * is open: the address does (`/rezervacije/:id`), but an id is not what the model needs
 * to say the number back, and half of these screens have the entity and not the id in
 * the path at all — the rota is a week, the chessboard a strip somebody clicked.
 *
 * So a view SAYS what it is showing, in three fields, and the drawer shows the same
 * three fields back to the person as a chip they can drop. What the service does with
 * it is one line of the system prompt and nothing else; an explicit entity in the
 * question always wins (`Logic.chatContextLine`).
 *
 * # How a view uses it
 *
 * ```ts
 * useAssistantPageContext(() => reservation.value && ({
 *     kind:  AssistantContextKind.Reservation,
 *     ids:   { reservation_id: reservation.value.id },
 *     label: `${reservation.value.number} · ${payerName.value}`,
 * }))
 * ```
 *
 * The source is re-read whenever its dependencies change, so a screen that loads its
 * entity a moment after it mounts needs no second call. Returning nothing means "I have
 * nothing to say about this" — before the entity has loaded, or, for a dialog, while it
 * is shut.
 *
 * # Why a stack and not one value
 *
 * Because these things nest, and the innermost open one is the answer. The reservation
 * page says it is about a booking; the check-out ladder OPENED on top of it says it is
 * about that booking's departure, and while it is open that is the truer sentence. A
 * single slot would have made this an ordering puzzle between a parent and a child that
 * both set up in the same tick — and Vue sets the child up second, so the shut dialog
 * would have won and published nothing at all.
 *
 * So every caller adds a source and the LAST one with something to say is what the
 * assistant is told. A dialog closing hands the page back its own sentence, and a view
 * leaving takes only its own away.
 *
 * # Why a module-level ref and not provide/inject
 *
 * The reader is the top bar's drawer, which is mounted BESIDE the router view rather
 * than under it — inject would have to reach across the layout, which is what a store
 * is for. It is not in `stores/app.ts` because nothing here belongs to the account or
 * survives a navigation: it is one fact about the screen that is open this second.
 */
import { computed, onUnmounted, ref, watch } from 'vue'

import type { IAssistantContext } from '@/types/Chat'

interface ContextSource {
    id:  number
    get: () => IAssistantContext | null
}

const sources = ref<ContextSource[]>([])

let nextId = 0

/** The innermost screen that has something to say, or null when none has. */
const context = computed((): IAssistantContext | null => {
    for (let i = sources.value.length - 1; i >= 0; i--) {
        const value = sources.value[i].get()

        if (value) return value
    }

    return null
})

/**
 * Whether the person has dropped the chip for THIS subject.
 *
 * Per subject and not per session: dropping "R-20260901-0001" says that this question
 * is not about that booking, and it must not go on suppressing the hint after the desk
 * has walked to another room.
 */
const dismissedKey = ref('')

/** Identity of a context, for the dismissal above. Kind plus ids, never the label —
 *  a guest renamed mid-stay is the same subject. */
const keyOf = (value: IAssistantContext | null): string => value
    ? `${value.kind}:${Object.entries(value.ids).sort().map(([key, id]) => `${key}=${id}`).join(',')}`
    : ''

// A changed subject is a changed screen as far as the chip is concerned: the dismissal
// was about the old one and must not silently cover the new one.
watch(context, value => {
    if (keyOf(value) !== dismissedKey.value) dismissedKey.value = ''
})

/**
 * The context as it should travel with a turn, and as the chip should draw it —
 * nothing when the screen says nothing about itself, and nothing when the person has
 * dropped it. One function for both, so the chip cannot show a subject that is not
 * being sent, which would be worse than no chip at all.
 */
export const assistantContextForTurn = (): IAssistantContext | undefined =>
    context.value && dismissedKey.value !== keyOf(context.value) ? context.value : undefined

/** Drops the hint for the subject that is open. The next screen brings its own back. */
export const dismissAssistantContext = () => { dismissedKey.value = keyOf(context.value) }

/**
 * Publishes what this view or dialog is about, for as long as it is on screen.
 *
 * `source` returns the context, or a falsy value while there is nothing to say — before
 * the entity has loaded, when the thing it was about has been deselected, or, in a
 * dialog, whenever it is shut.
 */
export const useAssistantPageContext = (source: () => IAssistantContext | null | undefined | false) => {
    const entry: ContextSource = { id: ++nextId, get: () => source() || null }

    sources.value.push(entry)

    onUnmounted(() => {
        const at = sources.value.findIndex(one => one.id === entry.id)

        if (at >= 0) sources.value.splice(at, 1)
    })
}
